import { STAGE_LABELS } from "@lib/types";
import { X } from "lucide-react";
import { useEffect, useState } from "react";

interface Props {
  open: boolean;
  candidateName: string;
  fromStage: string;
  onClose: () => void;
  onConfirm: (reason: string) => void | Promise<void>;
}

export function ReturnToPoolDialog({ open, candidateName, fromStage, onClose, onConfirm }: Props) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason("");
      setBusy(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const trimmed = reason.trim();
  const submitDisabled = trimmed.length === 0 || busy;

  const handleSubmit = async () => {
    if (submitDisabled) return;
    setBusy(true);
    try {
      await onConfirm(trimmed);
    } finally {
      setBusy(false);
    }
  };

  const stageLabel = STAGE_LABELS[fromStage] ?? fromStage;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="return-to-pool-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <button
        type="button"
        aria-label="Close return-to-pool dialog"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />
      <div className="relative z-50 w-full max-w-lg rounded-lg bg-card p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <div>
            <h3 id="return-to-pool-title" className="text-lg font-semibold text-text-primary">
              Return {candidateName} to pool
            </h3>
            <p className="mt-1 text-sm text-text-muted">
              Currently in {stageLabel}. They'll be removed from the pipeline and become invisible
              to the client.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded p-1 text-text-faint hover:bg-card-hover"
          >
            <X size={16} />
          </button>
        </div>
        <label className="mt-4 block">
          <span className="text-xs font-medium text-text-secondary">Reason</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            placeholder="e.g. lost interest, no longer relevant for this role"
            className="mt-1 w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </label>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitDisabled}
            className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Return to pool
          </button>
        </div>
      </div>
    </div>
  );
}
